import { MAGIC_LINK_TTL_MS } from './magicLinkFoundation';
import { APP_ENTRY_INTENTS, readBuyerGatewayOutboundHandoff } from './appEntryIntentResolver';

const GOOGLE_REDIRECT_INTENT_KEY = 'cc_google_redirect_intent';
const GOOGLE_AUTH_ATTEMPT_KEY = 'cc_google_auth_attempt';
const AUTH_TRANSITION_LOCK_KEY = 'cc_auth_transition_lock';
const MAGIC_LINK_ATTEMPT_KEY = 'cc_magic_link_attempt';
const BUYER_GATEWAY_RETURN_INTENT_KEY = 'cc_buyer_gateway_return_intent';
const PRE_REACT_PUBLIC_LANDING_KEY = 'cc_force_public_landing_once';

const DEFAULT_GOOGLE_PENDING_TTL_MS = 5 * 60 * 1000;
const DEFAULT_AUTH_TRANSITION_TTL_MS = 120000;
const DEFAULT_MAGIC_LINK_ATTEMPT_TTL_MS = MAGIC_LINK_TTL_MS.ATTEMPT;
export const BUYER_GATEWAY_RETURN_GRACE_MS = 3500;
const BUYER_GATEWAY_RETURN_TTL_MS = 30000;
const PRE_REACT_PUBLIC_LANDING_TTL_MS = 30000;

export const AUTH_ENTRY_TYPES = Object.freeze({
  MAGIC_LINK: 'magic_link',
  GOOGLE_REDIRECT_PENDING: 'google_redirect_return_pending',
  PWA_RESUME: 'pwa_resume',
  SERVICE_WORKER_HANDOFF: 'service_worker_handoff',
  AUTH_OBSERVING: 'auth_observing',
  BUYER_GATEWAY_X_CLOSE_OR_RETURN: 'buyer_gateway_x_close_or_return',
  BUYER_GATEWAY_RETURN_TO_MAIN: 'buyer_gateway_return_to_main',
  BUYER_GATEWAY_RETURN_PUBLIC_LANDING: 'buyer_gateway_return_public_landing',
  LOGGED_OUT_CONFIRMED: 'logged_out_confirmed',
});

function safeReadJson(storage, key) {
  try {
    const raw = storage?.getItem?.(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function safeWriteJson(storage, key, value) {
  try { storage?.setItem?.(key, JSON.stringify(value)); } catch (_) {}
}

function safeRemove(storage, key) {
  try { storage?.removeItem?.(key); } catch (_) {}
}

function readAnyStorageJson(key) {
  if (typeof window === 'undefined') return null;
  return safeReadJson(window.sessionStorage, key) || safeReadJson(window.localStorage, key);
}

function getStartedAt(payload) {
  return Number(payload?.startedAt || payload?.at || 0) || 0;
}

function getGuardStartedAt(payload) {
  return Number(payload?.returnObservedAt || payload?.startedAt || payload?.at || 0) || 0;
}

function isFresh(payload, ttlMs) {
  const startedAt = getStartedAt(payload);
  if (!startedAt) return false;
  const expiresAt = Number(payload?.expiresAt || 0);
  if (expiresAt && Date.now() > expiresAt) return false;
  return Date.now() - startedAt < Number(ttlMs || DEFAULT_GOOGLE_PENDING_TTL_MS);
}

function isGooglePayload(payload) {
  const raw = `${payload?.provider || ''} ${payload?.source || ''} ${payload?.phase || ''} ${payload?.mode || ''} ${payload?.status || ''}`.toLowerCase();
  return raw.includes('google');
}

function hasEmailLinkHint(href = '') {
  const raw = String(href || '');
  return raw.includes('emailLink=1') || raw.includes('mode=signIn') || raw.includes('oobCode=');
}

function isMagicLinkAttemptPayload(payload) {
  if (!payload?.attemptId) return false;
  const raw = `${payload?.source || ''} ${payload?.phase || ''} ${payload?.mode || ''} ${payload?.status || ''} ${payload?.provider || ''}`.toLowerCase();
  return raw.includes('magic') || raw.includes('email') || raw.includes('link');
}

function readValidatedMagicLinkAttempt(currentHref = '', { ttlMs = DEFAULT_MAGIC_LINK_ATTEMPT_TTL_MS, clearStale = true } = {}) {
  if (typeof window === 'undefined') return null;
  const hasUrlEvidence = hasEmailLinkHint(currentHref || window.location?.href || '');
  const readFresh = (storage) => {
    const payload = safeReadJson(storage, MAGIC_LINK_ATTEMPT_KEY);
    if (!payload) return null;
    const startedAt = getStartedAt(payload);
    const expiresAt = Number(payload?.expiresAt || 0);
    const isFreshAttempt = !!startedAt && (!expiresAt || Date.now() <= expiresAt) && Date.now() - startedAt < Number(ttlMs || DEFAULT_MAGIC_LINK_ATTEMPT_TTL_MS);
    const isMagicAttempt = isMagicLinkAttemptPayload(payload);

    // Phase 6C: storage-only Magic Link markers are not enough to seize
    // first paint. Without an actual email-link URL or an in-memory flow
    // passed through isMagicLink, stale markers must not drive Restore.
    if (!hasUrlEvidence || !isFreshAttempt || !isMagicAttempt) {
      if (clearStale) safeRemove(storage, MAGIC_LINK_ATTEMPT_KEY);
      return null;
    }
    return payload;
  };
  return readFresh(window.sessionStorage) || readFresh(window.localStorage);
}

export function hasBuyerGatewayReturnHint(href = '') {
  try {
    const url = new URL(String(href || ''), typeof window !== 'undefined' ? window.location.origin : 'https://cassavachain.com');
    const entry = String(url.searchParams.get('entry') || '').toLowerCase();
    const source = String(url.searchParams.get('source') || '').toLowerCase();
    const intent = String(url.searchParams.get('intent') || '').toLowerCase();
    const from = String(url.searchParams.get('from') || '').toLowerCase();
    const gatewayReturn = String(url.searchParams.get('gatewayReturn') || url.searchParams.get('gateway_return') || '').toLowerCase();
    const returnMain = String(url.searchParams.get('returnMain') || url.searchParams.get('return_main') || '').toLowerCase();
    const returnValue = String(url.searchParams.get('return') || '').toLowerCase();
    return (
      entry === 'buyer_gateway_return' ||
      (source === 'buyer_gateway' && intent === 'return_main') ||
      (from === 'buyer' && returnValue === 'main') ||
      gatewayReturn === '1' ||
      gatewayReturn === 'true' ||
      returnMain === '1' ||
      returnMain === 'true'
    );
  } catch (_) {
    const raw = String(href || '').toLowerCase();
    return (
      raw.includes('entry=buyer_gateway_return') ||
      (raw.includes('source=buyer_gateway') && raw.includes('intent=return_main')) ||
      (raw.includes('from=buyer') && raw.includes('return=main')) ||
      raw.includes('gatewayreturn=1') ||
      raw.includes('gateway_return=1') ||
      raw.includes('returnmain=1') ||
      raw.includes('return_main=1')
    );
  }
}

export function readBuyerGatewayReturnIntent({ ttlMs = BUYER_GATEWAY_RETURN_TTL_MS } = {}) {
  if (typeof window === 'undefined') return null;
  const now = Date.now();
  const readFresh = (storage) => {
    const payload = safeReadJson(storage, BUYER_GATEWAY_RETURN_INTENT_KEY);
    const startedAt = Number(payload?.startedAt || 0);
    const expiresAt = Number(payload?.expiresAt || 0);
    if (!payload || !startedAt || (expiresAt && now > expiresAt) || now - startedAt > ttlMs) {
      if (payload) safeRemove(storage, BUYER_GATEWAY_RETURN_INTENT_KEY);
      return null;
    }
    return payload;
  };
  return readFresh(window.sessionStorage) || readFresh(window.localStorage);
}

export function observeBuyerGatewayReturnIntent(href = '') {
  if (typeof window === 'undefined') return null;
  if (!hasBuyerGatewayReturnHint(href || window.location?.href || '')) return readBuyerGatewayReturnIntent();
  const existing = readBuyerGatewayReturnIntent();
  if (existing?.startedAt) return existing;
  const payload = {
    source: 'buyer_gateway',
    entryType: 'buyer_gateway_return_to_main',
    startedAt: Date.now(),
    expiresAt: Date.now() + BUYER_GATEWAY_RETURN_TTL_MS,
    allowLoginAfterAuthSettled: true,
    shouldShortCircuitLongRestore: true,
  };
  safeWriteJson(window.sessionStorage, BUYER_GATEWAY_RETURN_INTENT_KEY, payload);
  return payload;
}

export function clearBuyerGatewayReturnIntent() {
  if (typeof window === 'undefined') return;
  safeRemove(window.sessionStorage, BUYER_GATEWAY_RETURN_INTENT_KEY);
  safeRemove(window.localStorage, BUYER_GATEWAY_RETURN_INTENT_KEY);
}

export function cleanupBuyerGatewayReturnUrl() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!hasBuyerGatewayReturnHint(url.href)) return;
    if (url.searchParams.get('entry') === 'buyer_gateway_return') url.searchParams.delete('entry');
    if (url.searchParams.get('source') === 'buyer_gateway' && url.searchParams.get('intent') === 'return_main') {
      url.searchParams.delete('source');
      url.searchParams.delete('intent');
    }
    if (String(url.searchParams.get('from') || '').toLowerCase() === 'buyer' && String(url.searchParams.get('return') || '').toLowerCase() === 'main') {
      url.searchParams.delete('from');
      url.searchParams.delete('return');
    }
    ['gatewayReturn', 'gateway_return', 'returnMain', 'return_main'].forEach((key) => {
      const value = String(url.searchParams.get(key) || '').toLowerCase();
      if (value === '1' || value === 'true') url.searchParams.delete(key);
    });
    const next = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash || ''}`;
    window.history.replaceState({}, document.title, next || '/');
  } catch (_) {}
}


function writePreReactPublicLandingMarker(payload) {
  if (typeof window === 'undefined') return null;
  const normalized = {
    type: AUTH_ENTRY_TYPES.BUYER_GATEWAY_RETURN_PUBLIC_LANDING,
    forcePublicLanding: true,
    source: 'buyer_gateway',
    intent: 'return_main',
    createdAt: Date.now(),
    expiresAt: Date.now() + PRE_REACT_PUBLIC_LANDING_TTL_MS,
    ...(payload || {}),
  };
  safeWriteJson(window.sessionStorage, PRE_REACT_PUBLIC_LANDING_KEY, normalized);
  // Keep the marker in localStorage as a short-TTL fallback so a deployment
  // handoff / service-worker reload cannot consume the public landing guard
  // and then fall back into Workspace Restore. The marker is explicit,
  // time-bounded, and cleared by clearPreReactPublicLandingMarker().
  safeWriteJson(window.localStorage, PRE_REACT_PUBLIC_LANDING_KEY, normalized);
  try { window.__CASSAVACHAIN_BOOTSTRAP_ENTRY__ = normalized; } catch (_) {}
  return normalized;
}

function cleanBuyerGatewayReturnUrl(href) {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(String(href || window.location.href || ''), window.location.origin);
    if (!hasBuyerGatewayReturnHint(url.href)) return;
    if (url.searchParams.get('entry') === 'buyer_gateway_return') url.searchParams.delete('entry');
    if (url.searchParams.get('source') === 'buyer_gateway' && url.searchParams.get('intent') === 'return_main') {
      url.searchParams.delete('source');
      url.searchParams.delete('intent');
    }
    if (String(url.searchParams.get('from') || '').toLowerCase() === 'buyer' && String(url.searchParams.get('return') || '').toLowerCase() === 'main') {
      url.searchParams.delete('from');
      url.searchParams.delete('return');
    }
    ['gatewayReturn', 'gateway_return', 'returnMain', 'return_main'].forEach((key) => {
      const value = String(url.searchParams.get(key) || '').toLowerCase();
      if (value === '1' || value === 'true') url.searchParams.delete(key);
    });
    const next = `${url.pathname || '/'}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash || ''}`;
    window.history.replaceState({}, document?.title || '', next || '/');
  } catch (_) {}
}

export function clearPreReactPublicLandingMarker() {
  if (typeof window === 'undefined') return;
  safeRemove(window.sessionStorage, PRE_REACT_PUBLIC_LANDING_KEY);
  safeRemove(window.localStorage, PRE_REACT_PUBLIC_LANDING_KEY);
  try { delete window.__CASSAVACHAIN_BOOTSTRAP_ENTRY__; } catch (_) { try { window.__CASSAVACHAIN_BOOTSTRAP_ENTRY__ = null; } catch (_) {} }
}

export function readPreReactPublicLandingMarker({ consume = false, ttlMs = PRE_REACT_PUBLIC_LANDING_TTL_MS } = {}) {
  if (typeof window === 'undefined') return null;
  const fromGlobal = (() => {
    try { return window.__CASSAVACHAIN_BOOTSTRAP_ENTRY__ || null; } catch (_) { return null; }
  })();
  const payload = fromGlobal || safeReadJson(window.sessionStorage, PRE_REACT_PUBLIC_LANDING_KEY) || safeReadJson(window.localStorage, PRE_REACT_PUBLIC_LANDING_KEY);
  const createdAt = Number(payload?.createdAt || payload?.startedAt || 0);
  const expiresAt = Number(payload?.expiresAt || 0);
  const now = Date.now();
  const fresh = !!payload?.forcePublicLanding && !!createdAt && (!expiresAt || now <= expiresAt) && now - createdAt <= ttlMs;
  if (!fresh) {
    if (payload) clearPreReactPublicLandingMarker();
    return null;
  }
  if (hasEmailLinkHint(window.location?.href || '')) return null;
  if (consume) clearPreReactPublicLandingMarker();
  return payload;
}


export function hasBuyerGatewayPublicLandingProtection({ href, ttlMs = PRE_REACT_PUBLIC_LANDING_TTL_MS } = {}) {
  if (typeof window === 'undefined') return false;
  const currentHref = href || window.location?.href || '';
  if (hasEmailLinkHint(currentHref)) return false;
  if (hasBuyerGatewayReturnHint(currentHref)) return true;
  if (readPreReactPublicLandingMarker({ consume: false, ttlMs })?.forcePublicLanding) return true;
  if (readBuyerGatewayReturnIntent({ ttlMs })?.source === 'buyer_gateway') return true;
  try {
    const state = window.history?.state || null;
    const raw = `${state?.entry || ''} ${state?.source || ''} ${state?.intent || ''}`.toLowerCase();
    return raw.includes('buyer_gateway_return') || (raw.includes('buyer_gateway') && raw.includes('return_main'));
  } catch (_) {
    return false;
  }
}

export function protectBuyerGatewayPublicLanding({ href, reason = 'buyer-gateway-public-landing-protect' } = {}) {
  if (typeof window === 'undefined') return null;
  const currentHref = href || window.location?.href || '';
  if (hasEmailLinkHint(currentHref)) return null;
  const alreadyProtected = readPreReactPublicLandingMarker({ consume: false }) || null;
  if (alreadyProtected?.forcePublicLanding) return alreadyProtected;
  if (!hasBuyerGatewayReturnHint(currentHref)) return null;
  const payload = writePreReactPublicLandingMarker({ reason });
  try {
    const intentPayload = {
      source: 'buyer_gateway',
      entryType: 'buyer_gateway_return_to_main',
      startedAt: Date.now(),
      expiresAt: Date.now() + BUYER_GATEWAY_RETURN_TTL_MS,
      allowLoginAfterAuthSettled: true,
      shouldShortCircuitLongRestore: true,
      protectedFirstPaint: true,
      reason,
    };
    safeWriteJson(window.sessionStorage, BUYER_GATEWAY_RETURN_INTENT_KEY, intentPayload);
    safeWriteJson(window.localStorage, BUYER_GATEWAY_RETURN_INTENT_KEY, intentPayload);
  } catch (_) {}
  cleanBuyerGatewayReturnUrl(currentHref);
  return payload;
}

export function consumePreReactEntryIntent({ href, googlePendingTtlMs = DEFAULT_GOOGLE_PENDING_TTL_MS } = {}) {
  if (typeof window === 'undefined') return null;
  const currentHref = href || window.location?.href || '';
  if (!hasBuyerGatewayReturnHint(currentHref)) return null;

  // Magic Link must own first paint. Never clean or consume Firebase email-link URLs here.
  if (hasEmailLinkHint(currentHref)) return null;

  // Google redirect return must keep its paint guard. Buyer return intent must not override it.
  const googleMarkers = readGoogleEntryMarkers({ googlePendingTtlMs });
  if (googleMarkers?.hasGooglePending) {
    return {
      type: AUTH_ENTRY_TYPES.GOOGLE_REDIRECT_PENDING,
      forcePublicLanding: false,
      reason: googleMarkers.reason || 'google-pending-before-buyer-return',
      attemptId: googleMarkers.attemptId || '',
    };
  }

  return protectBuyerGatewayPublicLanding({
    href: currentHref,
    reason: 'pre-react-buyer-gateway-return',
  });
}

export function markGoogleReturnObserved({ googlePendingTtlMs = DEFAULT_GOOGLE_PENDING_TTL_MS } = {}) {
  if (typeof window === 'undefined') return;
  const touch = (storage, key) => {
    try {
      const payload = safeReadJson(storage, key);
      if (!payload || !isGooglePayload(payload) || !isFresh(payload, googlePendingTtlMs)) return;
      if (payload.returnObservedAt) return;
      storage?.setItem?.(key, JSON.stringify({ ...payload, returnObservedAt: Date.now() }));
    } catch (_) {}
  };
  [window.sessionStorage, window.localStorage].forEach((storage) => {
    touch(storage, GOOGLE_AUTH_ATTEMPT_KEY);
    touch(storage, GOOGLE_REDIRECT_INTENT_KEY);
    touch(storage, AUTH_TRANSITION_LOCK_KEY);
  });
}

export function readGoogleEntryMarkers({ googlePendingTtlMs = DEFAULT_GOOGLE_PENDING_TTL_MS } = {}) {
  const attempt = readAnyStorageJson(GOOGLE_AUTH_ATTEMPT_KEY);
  const intent = readAnyStorageJson(GOOGLE_REDIRECT_INTENT_KEY);
  const lock = readAnyStorageJson(AUTH_TRANSITION_LOCK_KEY);

  const freshAttempt = attempt && isGooglePayload(attempt) && isFresh(attempt, googlePendingTtlMs) ? attempt : null;
  const freshIntent = intent && isGooglePayload(intent) && isFresh(intent, googlePendingTtlMs) ? intent : null;
  const freshLock = lock && isGooglePayload(lock) && isFresh(lock, Math.max(googlePendingTtlMs, DEFAULT_AUTH_TRANSITION_TTL_MS)) ? lock : null;

  const marker = freshAttempt || freshIntent || freshLock || null;
  const startedAt = Math.min(
    ...[getGuardStartedAt(freshAttempt), getGuardStartedAt(freshIntent), getGuardStartedAt(freshLock)].filter(Boolean)
  );

  return {
    attempt: freshAttempt,
    intent: freshIntent,
    lock: freshLock,
    marker,
    hasGooglePending: !!marker,
    attemptId: marker?.attemptId || freshAttempt?.attemptId || freshIntent?.attemptId || freshLock?.attemptId || '',
    startedAt: Number.isFinite(startedAt) ? startedAt : getGuardStartedAt(marker),
    reason: freshAttempt ? 'fresh-google-auth-attempt' : freshIntent ? 'fresh-google-redirect-intent' : freshLock ? 'fresh-google-transition-lock' : '',
  };
}

export function isPwaStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    return !!(
      window.navigator?.standalone ||
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.matchMedia?.('(display-mode: fullscreen)')?.matches ||
      window.matchMedia?.('(display-mode: minimal-ui)')?.matches
    );
  } catch (_) {
    return false;
  }
}

export function classifyAuthEntry({
  href,
  isMagicLink = false,
  authReady = false,
  authNullSettled = false,
  user = null,
  currentUser = null,
  redirectChecked = false,
  loading = false,
  authRestorePending = false,
  googlePendingTtlMs = DEFAULT_GOOGLE_PENDING_TTL_MS,
  allowGooglePendingEntry = false,
} = {}) {
  const currentHref = href || (typeof window !== 'undefined' ? window.location?.href || '' : '');
  const hasMagicLinkUrl = hasEmailLinkHint(currentHref);
  const magicLinkStorage = readValidatedMagicLinkAttempt(currentHref);
  const magicLinkActive = !!isMagicLink || hasMagicLinkUrl || !!magicLinkStorage?.attemptId;
  if (magicLinkActive) {
    return {
      type: AUTH_ENTRY_TYPES.MAGIC_LINK,
      reason: isMagicLink ? 'active-magic-link-flow' : hasMagicLinkUrl ? 'magic-link-url' : 'fresh-magic-link-attempt',
      attemptId: magicLinkStorage?.attemptId || '',
    };
  }

  const googleMarkers = readGoogleEntryMarkers({ googlePendingTtlMs });
  if (allowGooglePendingEntry && !user && !currentUser && googleMarkers.hasGooglePending) {
    return {
      type: AUTH_ENTRY_TYPES.GOOGLE_REDIRECT_PENDING,
      reason: googleMarkers.reason || 'google-return-pending',
      attemptId: googleMarkers.attemptId,
      startedAt: googleMarkers.startedAt,
      google: googleMarkers,
    };
  }

  const preReactPublicLanding = readPreReactPublicLandingMarker({ consume: false });
  if (preReactPublicLanding?.forcePublicLanding) {
    return {
      type: AUTH_ENTRY_TYPES.BUYER_GATEWAY_RETURN_PUBLIC_LANDING,
      reason: preReactPublicLanding.reason || 'buyer-gateway-return-public-landing',
      source: 'buyer_gateway',
      startedAt: Number(preReactPublicLanding.createdAt || preReactPublicLanding.startedAt || Date.now()),
      forcePublicLanding: true,
      allowLoginAfterAuthSettled: true,
    };
  }

  if (hasBuyerGatewayReturnHint(currentHref)) {
    const consumed = consumePreReactEntryIntent({ href: currentHref, googlePendingTtlMs });
    if (consumed?.forcePublicLanding) {
      return {
        type: AUTH_ENTRY_TYPES.BUYER_GATEWAY_RETURN_PUBLIC_LANDING,
        reason: 'buyer-gateway-return-public-landing-url',
        source: 'buyer_gateway',
        startedAt: Number(consumed.createdAt || Date.now()),
        forcePublicLanding: true,
        allowLoginAfterAuthSettled: true,
      };
    }
  }

  const outboundBuyerGatewayHandoff = readBuyerGatewayOutboundHandoff({ consume: false });
  if (outboundBuyerGatewayHandoff?.intent === APP_ENTRY_INTENTS.BUYER_GATEWAY_OUTBOUND_HANDOFF) {
    const publicSource = outboundBuyerGatewayHandoff.sourceContext !== 'protected_workspace';
    return {
      type: publicSource
        ? AUTH_ENTRY_TYPES.BUYER_GATEWAY_RETURN_PUBLIC_LANDING
        : AUTH_ENTRY_TYPES.BUYER_GATEWAY_X_CLOSE_OR_RETURN,
      reason: publicSource ? 'buyer-gateway-x-close-public-return' : 'buyer-gateway-x-close-protected-return',
      source: 'buyer_gateway_outbound_handoff',
      sourceContext: outboundBuyerGatewayHandoff.sourceContext,
      startedAt: outboundBuyerGatewayHandoff.startedAt,
      forcePublicLanding: publicSource,
      allowLoginAfterAuthSettled: publicSource,
    };
  }

  if (isPwaStandalone() && (!authReady || authRestorePending || loading || !redirectChecked)) {
    return { type: AUTH_ENTRY_TYPES.PWA_RESUME, reason: 'pwa-auth-observing' };
  }

  if (!authReady || !redirectChecked || authRestorePending || loading) {
    return { type: AUTH_ENTRY_TYPES.AUTH_OBSERVING, reason: 'auth-entry-observing' };
  }

  if (!user && !currentUser && authReady && authNullSettled) {
    return { type: AUTH_ENTRY_TYPES.LOGGED_OUT_CONFIRMED, reason: 'auth-null-confirmed' };
  }

  return { type: AUTH_ENTRY_TYPES.AUTH_OBSERVING, reason: 'default-auth-observing' };
}

export function isAuthEntryPending(entry) {
  return [
    AUTH_ENTRY_TYPES.MAGIC_LINK,
    AUTH_ENTRY_TYPES.GOOGLE_REDIRECT_PENDING,
    AUTH_ENTRY_TYPES.BUYER_GATEWAY_X_CLOSE_OR_RETURN,
    AUTH_ENTRY_TYPES.PWA_RESUME,
    AUTH_ENTRY_TYPES.SERVICE_WORKER_HANDOFF,
    AUTH_ENTRY_TYPES.AUTH_OBSERVING,
  ].includes(entry?.type);
}
